// All copy for the Longevity Checklist page and its privacy policy lives here.
// Edit text freely; the markup in LongevityPage.js and LongevityPrivacyPage.js
// reads from this file. Keep the `appName` and `promise` keys on one line each:
// scripts/longevity-images.py reads them to draw the Open Graph image.

export const APP_STORE_ID = '6737241919';
export const APP_STORE_URL =
  'https://apps.apple.com/us/app/longevity-checklist/id6737241919?itscg=30200&itsct=apps_box_badge&mttnsubad=6737241919';
export const SITE_URL = 'https://taci.uk';
export const PAGE_PATH = '/longevitychecklist';
export const PRIVACY_PATH = '/longevitychecklist/privacy';
export const SUPPORT_EMAIL = 'support@taci.uk';
export const BUNDLE_ID = 'com.michaeltaciuk.longevityChecklist';

export const content = {
  appName: 'Longevity Checklist',
  promise: 'A short daily checklist for a longer life.',
  copyright: 'Michael Taciuk',

  meta: {
    title: 'Longevity Checklist for iPhone',
    description:
      'A calm, minimal habit app built around the few daily behaviours with the strongest evidence for a longer, healthier life. Personalised checklist, sleep routine, streaks with rest days.',
    privacyTitle: 'Privacy Policy for Longevity Checklist',
    privacyDescription:
      'How Longevity Checklist handles your data: everything stays on your device, Apple Health is read-only, and analytics are anonymous.',
  },

  nav: {
    links: [
      { label: 'Features', href: '#features' },
      { label: 'Widgets', href: '#widgets' },
      { label: 'Privacy', href: '#privacy' },
    ],
    compactCta: 'Get the app',
  },

  // Shown on desktop when the nav button is clicked.
  qr: {
    label: 'iPhone app',
    heading: 'Scan to download',
    body: 'Point your iPhone camera at the code to open Longevity Checklist on the App Store.',
    link: 'Or open the App Store page',
    alt: 'QR code linking to Longevity Checklist on the App Store.',
  },

  hero: {
    supporting:
      'Answer five questions and get a personalised daily list, long-term goals and an evening routine timed to your bedtime.',
    imageAlt:
      'The Daily Checklist screen showing a 23-day streak with 3 of 5 items done, and items for diet, sleep, light exercise, 8,000 steps and morning sunlight.',
  },

  howItWorks: {
    label: 'How it works',
    heading: 'Three steps, then a minute a day.',
    steps: [
      {
        title: 'Answer five questions',
        body: 'Age, exercise level, smoking, body weight and wake-up time. That is all it needs.',
      },
      {
        title: 'Get your checklist',
        body: 'A daily list, a set of long-term goals and an evening routine built around your bedtime.',
      },
      {
        title: 'Check it off daily',
        body: 'Tick items as you go. Streaks grow, and one rest day a week keeps them safe.',
      },
    ],
  },

  features: {
    label: 'Features',
    heading: 'What you get',
    rows: [
      {
        id: 'checklist',
        label: 'Your checklist',
        heading: 'Built from your answers, then yours to edit.',
        intro: 'The list starts personal and stays simple.',
        points: [
          {
            title: 'Personalised list',
            body: 'Add, edit or delete any item, and attach your own description and source link.',
          },
          {
            title: 'Streaks with rest days',
            body: 'Habits that stick earn a flame. One missed day a week does not break a 23-day streak, so one bad day never erases months of work.',
          },
          {
            title: 'Steps from Apple Health',
            body: 'Step items tick themselves off when Apple Health reports the goal. Live progress like 6,412 / 8,000 steps shows under the item, and the goal is editable.',
          },
        ],
        image: 'daily-crop',
        imageAlt:
          'Close-up of the checklist: a 23-day streak card, best 41, 3 of 5 done, and the step item showing 6,412 of 8,000 steps.',
        framed: false,
      },
      {
        id: 'sleep',
        label: 'Sleep',
        heading: 'An evening routine that works back from your alarm.',
        paragraphs: [
          'Set a wake-up time and the app works back eight hours to a bedtime, then times the evening around it: caffeine cutoff, last meal, screens off, lights out.',
          'No alcohol, dim amber light and a cool room complete the routine, loosely based on the Blueprint evening routine. Switch on extras like a wind-down hour, a warm shower or mouth tape, remove anything you do not want, and keep a separate streak for nights.',
        ],
        image: 'sleep',
        imageAlt:
          'The Sleep screen: lights out 10:30 PM, wake 6:30 AM, a 12-night streak, and tonight’s routine from no caffeine after 12:30 PM through lights out by 10:30 PM.',
        framed: true,
      },
      {
        id: 'review',
        label: 'Weekly review',
        heading: 'Sunday, in one screen.',
        paragraphs: [
          'Every Sunday you get the week per habit, your perfect days and nights, the habit that stuck best and the one with room to grow.',
          'A 14-week history heatmap for any single item is one tap away.',
        ],
        image: 'review',
        imageAlt:
          'The weekly review: 6 of 7 perfect days, a 23-day streak, 6 perfect nights, a Monday-to-Sunday grid for each habit, and cards for the habit that stuck best and the one with room to grow.',
        framed: true,
      },
      {
        id: 'learn',
        label: 'Learn',
        heading: 'Know why, not just what.',
        paragraphs: [
          'Every item has a short, mechanistic explanation of why it matters, with a source. Your own items can carry a source link too.',
          'Caffeine has a half-life of five to six hours, which is why the cutoff sits early in the afternoon.',
        ],
        image: 'learn',
        imageAlt:
          'The Learn tab of the daily list with one item expanded to show its explanation and a source link.',
        framed: true,
      },
    ],
  },

  widgets: {
    label: 'Widgets',
    heading: 'Progress at a glance.',
    body: 'Today’s checklist with its streak, and today’s step count, on your Home Screen. No need to open the app to see where you stand.',
    cards: [
      {
        image: 'widget-checklist',
        caption: 'Today’s checklist',
        alt: 'The checklist widget showing today’s progress and the current streak.',
      },
      {
        image: 'widget-steps',
        caption: 'Today’s steps',
        alt: 'The steps widget showing 662 steps today.',
      },
    ],
    wide: {
      image: 'widget-steps-lockscreen',
      caption: 'Steps on the Lock Screen',
      alt: 'An iPhone Lock Screen with the steps widget showing 662 steps today, as an inline widget and a circular one.',
    },
    lockScreenNote: 'Both widgets work on the Lock Screen too.',
  },

  also: {
    label: 'Also in the app',
    heading: 'The rest, kept quiet.',
    cards: [
      {
        label: 'Long-term goals',
        heading: 'Bigger changes, tracked separately.',
        body: 'Quit smoking, reach a healthy body weight, or add your own. Mark them complete and keep an archive of what you have finished.',
      },
      {
        label: 'Reminders',
        heading: 'Only if you want them.',
        body: 'Optional daily, evening wind-down and Sunday review notifications, each at a time you choose.',
      },
      {
        label: 'Celebrations',
        heading: 'Noticed, not shouted.',
        body: 'A perfect day ignites the flame and pulses the checkboxes. A perfect week adds a sunrise wash, or a starry night for sleep. Milestones at 30, 50 and 100 days stamp the card, each with its own haptic. No confetti.',
      },
    ],
  },

  why: {
    label: 'Why these habits',
    heading: 'Small, boring, well evidenced.',
    facts: [
      'Caffeine’s half-life is five to six hours, so an afternoon coffee is still working at bedtime.',
      'A 1–2°F drop in core temperature is one of the triggers for sleep. A cool bedroom helps it along.',
      'Around 8,000 steps a day is where the benefit of walking largely levels off in large studies, so that is the default goal.',
    ],
    note: 'The Learn tab explains the science behind every item.',
  },

  privacy: {
    label: 'Privacy',
    heading: 'Yours, on your phone.',
    body: 'Everything is stored on your device. Health data is read, never written, and never leaves your phone. Analytics are anonymous and contain no health values or free text.',
    link: 'Read the privacy policy',
  },

  finalCta: {
    label: 'Get started',
    supporting: 'Free on the App Store for iPhone.',
  },

  footer: {
    privacy: 'Privacy policy',
    support: 'Support',
  },

  privacyPolicy: {
    updated: '6 October 2026',
    intro:
      'Longevity Checklist is designed so that your data stays with you. This page explains what the app stores, what it reads, and what leaves your phone. The short version: everything is stored on your device, Apple Health is read-only, and the only thing sent anywhere is anonymous usage analytics.',
    sections: [
      {
        heading: 'No accounts',
        paragraphs: [
          'The app has no sign-up, sign-in or cloud sync. It never asks for your name, email address or Apple ID, and it does not create a profile of you anywhere.',
        ],
      },
      {
        heading: 'What is stored on your device',
        paragraphs: [
          'Your onboarding answers (age, exercise level, whether you smoke, body-weight category and wake-up time), your checklist items and any descriptions or links you attach to them, your long-term goals, your sleep routine, your check-offs and streak history, and your settings.',
          'All of this lives only on your iPhone, in the app’s own storage. Deleting the app deletes it. If you use iCloud device backup, it is included in that backup like any other app data, under Apple’s terms.',
        ],
      },
      {
        heading: 'Apple Health',
        paragraphs: [
          'If you allow it, the app reads your step count from Apple Health so that step items can tick themselves off and show live progress. That is the only Health data it reads.',
          'The app never writes to Apple Health, and Health data never leaves your phone. It is not included in analytics. You can withdraw access at any time in the Health app under Sharing, Apps and Services.',
        ],
      },
      {
        heading: 'Analytics',
        paragraphs: [
          'The app sends anonymous performance and crash reports to Expo’s EAS Observe service so we can see whether the app launches quickly and whether it crashes. A report contains timings, any error that occurred, the app version, the platform and the device type, tagged with a random identifier that is unique to this install and resets if you delete and reinstall the app.',
          'Reports contain no health values, no check-off history, no free text you enter, and nothing that identifies you. Expo processes these reports on our behalf and keeps them for at least 60 days. They are never sold, never shared with anyone else, and never used for advertising.',
        ],
      },
      {
        heading: 'Notifications and widgets',
        paragraphs: [
          'Daily, evening and Sunday reminders are optional. They are scheduled locally on your phone and involve no server.',
          'The checklist and steps widgets read the same local data as the app and send nothing anywhere.',
        ],
      },
      {
        heading: 'Children',
        paragraphs: [
          'The app is not directed at children under 13 and collects no personal information from anyone.',
        ],
      },
      {
        heading: 'Your choices',
        paragraphs: [
          'Delete the app to remove everything it stores. Turn off Health access in the Health app. Turn off reminders in the app’s settings or in iOS Settings.',
        ],
      },
      {
        heading: 'This website',
        paragraphs: ['taci.uk uses Google Analytics to count visits and see which pages are read. It sets cookies in your browser and records the page you view, the site that linked you here, your browser, device type and approximate location (worked out from your IP address, which Google does not store). It is used only on this website, never inside the app, and is not linked to anything you do in the app. You can block it with a content blocker or Google’s opt-out add-on at tools.google.com/dlpage/gaoptout.'],
      },
      {
        heading: 'Changes to this policy',
        paragraphs: [
          'If the app’s handling of data changes, this page will change with it and the date above will be updated.',
        ],
      },
      {
        heading: 'Contact',
        paragraphs: [
          'Longevity Checklist is made by Michael Taciuk. Questions about privacy or the app can be sent to the support address below.',
        ],
        contact: true,
      },
      {
        heading: 'Not medical advice',
        paragraphs: [
          'Longevity Checklist provides general information to support healthy habits. It is not a substitute for professional medical advice, diagnosis or treatment. Always consult a qualified healthcare provider for advice about your own health.',
        ],
      },
    ],
  },
};
